import {
  Alert,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import { Box, Modal, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DocumentFile } from '../../generated/graphql';
import { useHttpClient } from '../../providers/HttpClientProvider';

const style = {
  // eslint-disable-next-line @typescript-eslint/prefer-as-const
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: 'calc(100vw - 24px)', sm: 'min(720px, 90vw)' },
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
  overflowY: 'scroll' as const,
};

interface UploadFileModalProps {
  handleClose: () => void;
  open: boolean;
  showLoading: boolean;
  extraData:
    | {
        routeName: string;
        legId: number;
        subcontractorName: string;
        existingFiles: DocumentFile[];
      }
    | undefined;
  onUploadStart: (legId: number, files: File[]) => Promise<void>;
  onDeleteClicked: (legId: number, fileId: number) => Promise<void>;
}

const MAX_IMAGE_COUNT = 5;
const MAX_FILE_SIZE_ALLOWED = 5 * 1024 * 1024;

export function UploadFileModal(props: UploadFileModalProps) {
  const {
    handleClose,
    open,
    showLoading,
    extraData,
    onUploadStart,
    onDeleteClicked,
  } = props;
  const { t } = useTranslation();
  const { httpClient } = useHttpClient();
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const existingFiles = extraData?.existingFiles ?? [];
  const remainingSlots = Math.max(
    0,
    MAX_IMAGE_COUNT - existingFiles.length - selectedFiles.length,
  );

  useEffect(() => {
    if (!open || !cameraOpen) return;

    let stream: MediaStream | undefined;
    let disposed = false;
    const startCamera = async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error('Camera access is not supported by this browser.');
        }
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
        if (disposed) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        if (videoRef.current) videoRef.current.srcObject = stream;
        setCameraError('');
      } catch (error: any) {
        setCameraError(error?.message || 'Unable to access the camera.');
        setCameraOpen(false);
      }
    };

    void startCamera();
    return () => {
      disposed = true;
      stream?.getTracks().forEach((track) => track.stop());
      if (videoRef.current) videoRef.current.srcObject = null;
    };
  }, [cameraOpen, open]);

  const handleChange = (files: FileList | null) => {
    setErrorMessage('');
    if (!files?.length) return;

    const incomingFiles = Array.from(files);
    if (
      incomingFiles.some(
        (file) =>
          !['image/jpeg', 'image/png'].includes(file.type) ||
          file.size > MAX_FILE_SIZE_ALLOWED,
      )
    ) {
      setErrorMessage('Choose JPEG or PNG images up to 5 MB each.');
    } else if (incomingFiles.length > remainingSlots) {
      setErrorMessage(
        `You can add ${remainingSlots} more image(s). Delete existing images to add more.`,
      );
    } else {
      setSelectedFiles((previous) => [...previous, ...incomingFiles]);
    }
    if (fileInput.current) fileInput.current.value = '';
  };

  const captureImage = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) return;

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    if (!context) return;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setErrorMessage('Could not capture the image. Please try again.');
          return;
        }
        if (blob.size > MAX_FILE_SIZE_ALLOWED) {
          setErrorMessage('Captured image exceeds the 5 MB file limit.');
          return;
        }
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        setSelectedFiles((previous) => [
          ...previous,
          new File([blob], `captured-${timestamp}.jpg`, { type: 'image/jpeg' }),
        ]);
        setErrorMessage('');
      },
      'image/jpeg',
      0.9,
    );
  };

  const uploadSelectedFiles = async () => {
    if (!extraData || selectedFiles.length === 0) return;
    try {
      await onUploadStart(extraData.legId, selectedFiles);
      setSelectedFiles([]);
      setErrorMessage('');
      setCameraOpen(false);
    } catch (error: any) {
      setErrorMessage(error?.message || 'Image upload failed. Please retry.');
    }
  };

  const downloadFile = async (file: (typeof existingFiles)[number]) => {
    try {
      const response = await httpClient.downloadFile(
        { path: file.path },
        'blob',
      );
      const url = window.URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = file.originalname;
      anchor.click();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      setErrorMessage(error?.message || 'Could not download this image.');
    }
  };

  const deleteFile = async (fileId: number) => {
    if (!extraData) return;
    setErrorMessage('');
    try {
      await onDeleteClicked(extraData.legId, fileId);
    } catch (error: any) {
      setErrorMessage(error?.message || 'Could not delete this image.');
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!showLoading) handleClose();
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      disableScrollLock
    >
      <Box sx={style}>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          width="100%"
        >
          <Typography variant="h6">{`${t('info.uploadImage')}`}</Typography>

          {!showLoading && (
            <IconButton style={{ marginLeft: 8 }} onClick={handleClose}>
              <CloseIcon fontSize="large" color="action" />
            </IconButton>
          )}
        </Box>

        {!showLoading && (
          <>
            <Typography
              style={{ fontSize: 15 }}
            >{`RouteName: ${extraData?.routeName}`}</Typography>
            <Typography
              style={{ fontSize: 13 }}
            >{`LegID: [${extraData?.legId}]`}</Typography>
            <Typography
              style={{ fontSize: 13 }}
            >{`Subcontractor: ${extraData?.subcontractorName}`}</Typography>
            <Divider style={{ marginTop: 5, marginBottom: 5 }} />
            <Typography style={{ fontSize: 13 }}>
              {existingFiles.length} of {MAX_IMAGE_COUNT} images attached. You
              can add {remainingSlots} more. Each image must be JPEG or PNG and
              no larger than 5 MB.
            </Typography>
            {remainingSlots === 0 && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Maximum of five images reached. Delete an existing image to add
                another.
              </Alert>
            )}

            <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<PhotoLibraryIcon />}
                disabled={remainingSlots === 0}
              >
                Choose images
                <input
                  ref={fileInput}
                  hidden
                  type="file"
                  multiple
                  onChange={(event) => handleChange(event.target.files)}
                  accept="image/png,image/jpeg"
                />
              </Button>
              <Button
                variant="outlined"
                startIcon={<CameraAltIcon />}
                onClick={() => {
                  setCameraError('');
                  setCameraOpen(true);
                }}
                disabled={remainingSlots === 0}
              >
                Open camera
              </Button>
              {selectedFiles.length > 0 && (
                <Button
                  variant="contained"
                  onClick={uploadSelectedFiles}
                  disabled={showLoading}
                >
                  Upload {selectedFiles.length} image(s)
                </Button>
              )}
            </Stack>

            {cameraError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {cameraError}
              </Alert>
            )}
            {errorMessage && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {errorMessage}
              </Alert>
            )}

            {cameraOpen && (
              <Box sx={{ mt: 2 }}>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  style={{
                    width: '100%',
                    maxHeight: 320,
                    objectFit: 'contain',
                  }}
                />
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button
                    variant="contained"
                    onClick={captureImage}
                    disabled={remainingSlots === 0}
                  >
                    Capture image ({remainingSlots} remaining)
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setCameraOpen(false)}
                  >
                    Stop camera
                  </Button>
                </Stack>
              </Box>
            )}

            {selectedFiles.length > 0 && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2">Ready to upload</Typography>
                <List dense>
                  {selectedFiles.map((file, index) => (
                    <ListItem
                      key={`${file.name}-${file.lastModified}-${index}`}
                    >
                      <ListItemText
                        primary={file.name}
                        secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                      />
                      <IconButton
                        aria-label={`Remove ${file.name}`}
                        onClick={() =>
                          setSelectedFiles((previous) =>
                            previous.filter(
                              (_, fileIndex) => fileIndex !== index,
                            ),
                          )
                        }
                      >
                        <CloseIcon />
                      </IconButton>
                    </ListItem>
                  ))}
                </List>
              </>
            )}

            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2">Attached images</Typography>
            {existingFiles.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No images attached.
              </Typography>
            ) : (
              <List dense>
                {existingFiles.map((file) => (
                  <ListItem key={file.id} divider>
                    <ListItemText
                      primary={file.originalname}
                      secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                    />
                    <Tooltip title="Download image">
                      <span>
                        <IconButton
                          aria-label={`Download ${file.originalname}`}
                          onClick={() => void downloadFile(file)}
                          disabled={showLoading}
                        >
                          <CloudDownloadIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete image">
                      <span>
                        <IconButton
                          aria-label={`Delete ${file.originalname}`}
                          color="error"
                          onClick={() => void deleteFile(file.id)}
                          disabled={showLoading}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </ListItem>
                ))}
              </List>
            )}
          </>
        )}

        {showLoading && (
          <CircularProgress style={{ marginTop: 5, alignSelf: 'center' }} />
        )}
      </Box>
    </Modal>
  );
}
